import { Middleware } from 'redux';
import { isAction } from '@reduxjs/toolkit';

import { componentDesignsDeleted, projectArchived, projectSynchronized } from '@paragon/layout-models';
import { enqueuePermanentError } from '@paragon/notification-tools';
import { isRemoteAction } from '@paragon/sync-client';

import { RootState } from '..';
import { forceClearSelection, moveToComponent } from '../actions/selectActions';
import { ReduxDispatch } from '../actions/thunkActions';
import { getSortedComponentDesigns } from '../selectors/getSortedComponentDesignsSelectors';

export const componentDesignSynchronizationMiddleware: Middleware<{}, RootState, ReduxDispatch> =
  (store) => (next) => (action) => {
    if (!isAction(action)) {
      return next(action);
    }

    const result = next(action);

    const {
      application: { openContext },
      componentDesigns: { data: componentDesigns, loadedState },
      insertions,
      selections: { activeComponentDesignGuid },
    } = store.getState();

    if (
      openContext?.type === 'project' &&
      projectArchived.match(action) &&
      action.payload.guid === openContext.projectGuid
    ) {
      store.dispatch(enqueuePermanentError('This project has been deleted from the Project Manager.'));
      return result;
    }

    if (
      isRemoteAction(action) &&
      projectSynchronized.match(action) &&
      openContext?.type === 'project' &&
      openContext.projectGuid in action.payload
    ) {
      const syncPayload = action.payload[openContext.projectGuid];

      // If the active component design was deleted, move on.
      if (
        activeComponentDesignGuid &&
        loadedState[activeComponentDesignGuid]?.committed &&
        !insertions.some((insertion) => insertion.objectGuid === activeComponentDesignGuid)
      ) {
        // Need to make sure the component we're moving to wasn't deleted as well.
        let nextValidComponentDesign = null;
        for (const componentDesign of getSortedComponentDesigns(store.getState())) {
          if (syncPayload.incomingInsertions.some((insertion) => insertion.objectGuid === componentDesign.guid)) {
            nextValidComponentDesign = componentDesign;
            break;
          }
        }

        if (nextValidComponentDesign) {
          store.dispatch(moveToComponent(nextValidComponentDesign));
        } else {
          store.dispatch(forceClearSelection());
        }
      }

      // Delete component designs that were previously in the project but are no longer part of it.
      for (const localComponentDesign of Object.values(componentDesigns)) {
        const olderVersionOfComponentWasRemoved = syncPayload.expiredModelGuids.includes(localComponentDesign.guid);

        const componentWasMoved = syncPayload.incomingInsertions.some(
          (insertion) => insertion.objectGuid === localComponentDesign.guid,
        );

        const componentWasModified = syncPayload.incomingModels.some(
          (model) => model.guid === localComponentDesign.guid,
        );

        if (olderVersionOfComponentWasRemoved && !(componentWasMoved || componentWasModified)) {
          store.dispatch(componentDesignsDeleted(localComponentDesign.guid));
        }
      }
    }

    return result;
  };
